import { AppModule } from '@/core/modules/app/app.module'
import { Analytics } from '@core/services/mixpanel'
import { SimpleMenu } from '@/ui/components/SimpleMenu'
import DownloadIcon from '@assets/images/download.svg?react'
import { useCustomTheme } from '@/core/theme'
import { FC, useMemo, useState } from 'react'
import { PdfExportWrapper } from './index.styles'
import { useIssuesExport } from './useIssuesExport'
import Typography from '@/ui/components/typography'

type ExportIssueOption = {
    value: string
    id: string
    disabled?: boolean
}

export const PdfIssuesExport: FC<{
    issues: TIssue[]
    filteredIssues: TIssue[]
}> = ({ issues, filteredIssues }) => {
    const {
        error,
        isFilterActive,
        loadingAll,
        loadingFiltered,
        disabledAll,
        disabledFiltered,
        downloadAll,
        downloadFiltered,
    } = useIssuesExport(issues, filteredIssues)
    const theme = useCustomTheme()
    const { scanInProgress } = AppModule.useStore()
    const [open, setOpen] = useState(false)

    const handleOnItemClick = (item: ExportIssueOption) => {
        if (!item.disabled) {
            const isFiltered = item.id === 'filtered-issues'
            Analytics.event('SEM::user_export_issues', { isFiltered })
            if (isFiltered) {
                downloadFiltered()
            } else {
                downloadAll()
            }
            setOpen(false)
        }
    }

    const exportIssuesOptions: ExportIssueOption[] = [
        { value: 'All Issues', id: 'all-issues' },
        {
            value: 'Filtered Issues',
            id: 'filtered-issues',
            disabled: !(isFilterActive && filteredIssues.length > 0),
        },
    ]

    const items = useMemo(
        () =>
            exportIssuesOptions.map((item) => ({
                id: item.id,
                value: item.value,
                active: !item.disabled,
                action: () => !item.disabled && handleOnItemClick(item),
            })),
        [exportIssuesOptions]
    )

    if (error) {
        console.error('[PdfIssuesExport] Error:', error)
        return null
    }

    const isDownloadButtonDisabled =
        scanInProgress || loadingAll || loadingFiltered

    const renderDisabledExportButton = () => {
        return (
            <PdfExportWrapper
                data-testid="pdf-export-button-loading"
                className="is-loading"
                onClick={(e) => e.preventDefault()}
            >
                <DownloadIcon
                    width={12}
                    height={12}
                    color={theme.colors.greys[400]}
                />
                <Typography
                    color={theme.colors.greys[400]}
                    variant="xs-regular"
                >
                    Download Report
                </Typography>
                <span className="export-spinner" />
            </PdfExportWrapper>
        )
    }

    const renderExportButton = () => {
        return (
            <PdfExportWrapper
                data-testid="pdf-export-button"
                onClick={() => !disabledAll && setOpen(!open)}
            >
                <DownloadIcon
                    width={12}
                    height={12}
                    color={theme.colors.primary[400]}
                />
                <Typography
                    color={theme.colors.primary[400]}
                    variant="xs-regular"
                >
                    Download Report
                </Typography>
                {(loadingAll || loadingFiltered) && (
                    <span className="export-spinner" />
                )}
            </PdfExportWrapper>
        )
    }

    return (
        <SimpleMenu
            open={open}
            onActivate={() => setOpen(!open)}
            onClose={() => setOpen(false)}
            activator={
                isDownloadButtonDisabled
                    ? renderDisabledExportButton()
                    : renderExportButton()
            }
            items={items}
            duration={0}
        />
    )
}
